import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";

const PAGE_SIZE = 20;
const SORTS = [
  { key: "followers", label: "Followers" },
  { key: "games", label: "Games" },
];

function UserAvatar({ hexes, name }) {
  const style = hexes
    ? { background: `linear-gradient(135deg, ${hexes[0]}, ${hexes[1]})` }
    : { background: "rgba(255, 255, 255, 0.08)" };
  return (
    <div className="admin-user-avatar" style={style} aria-hidden="true">
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

function UsersTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sort, setSort] = useState("followers");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE), sort });
      const data = await apiFetch(`/admin/social/users?${params.toString()}`);
      setItems(data.items);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, sort]);

  useEffect(() => {
    load();
  }, [load]);

  function selectSort(key) {
    setSort(key);
    setPage(1);
  }

  return (
    <div>
      <p className="kicker">Social network</p>
      <h2 className="admin-title admin-title--left">Users</h2>

      <div className="admin-links-search">
        <span className="admin-sort-label">Sort by</span>
        <div className="admin-linked-filter">
          {SORTS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`admin-tab admin-linked-filter-btn ${sort === s.key ? "admin-tab-active" : ""}`}
              onClick={() => selectSort(s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-table glass">
        {items && items.length === 0 && <p className="admin-status">No users yet.</p>}
        {items && items.map((item) => {
          const name = item.username || item.display_name || item.email;
          return (
            <div className="admin-row" key={item._id}>
              <UserAvatar hexes={item.avatar_hexes} name={name} />
              <div className="admin-row-main">
                <div className="admin-source-name">
                  {name}
                  {item.is_pro && <code className="admin-barcode-chip admin-platform-abbr">PRO</code>}
                </div>
                <div className="admin-source-meta">
                  {item.display_name && item.display_name !== name ? `${item.display_name} · ` : ""}{item.email}
                </div>
                <div className="admin-source-meta">
                  joined {new Date(item.createdAt).toLocaleDateString()}
                  {item.synced_at
                    ? <span className="admin-source-created">last sync {new Date(item.synced_at).toLocaleString()}</span>
                    : <span className="admin-source-created">never synced</span>}
                </div>
              </div>
              <div className="admin-row-meta admin-user-stats">
                <div className={`admin-user-stat ${sort === "games" ? "admin-user-stat--active" : ""}`}>
                  <span className="admin-user-stat-value">{item.total_games}</span>
                  <span className="admin-user-stat-label">games</span>
                </div>
                <div className="admin-user-stat">
                  <span className="admin-user-stat-value">{item.total_collection}</span>
                  <span className="admin-user-stat-label">collection</span>
                </div>
                <div className={`admin-user-stat ${sort === "followers" ? "admin-user-stat--active" : ""}`}>
                  <span className="admin-user-stat-value">{item.followers_count}</span>
                  <span className="admin-user-stat-label">followers</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default UsersTab;
